import React, { useEffect, Suspense, lazy } from 'react'
import {
    BrowserRouter as Router,
    Route,
    Switch,
    withRouter,
} from 'react-router-dom'
import { compose } from 'redux'
import WithErrors from './hocs/WithErrors'
import SiteSearch from './components/SiteSearch'
import lazySizes from 'lazysizes'
import 'lazysizes/plugins/attrchange/ls.attrchange'
import M from 'materialize-css'
import createACardAddToCart from './components/CreateACard/createACardAddToCart'
import addToCartErrorHandler from './components/CreateACard/addToCartErrorHandler'
import CreateACardFooterContent from './components/CreateACard/FooterContent'
import * as Sentry from '@sentry/react'
import SMBOrderFlowFooterContent from './components/SmallBusiness/OrderFlow/Layout/FooterContent'
import isEmpty from 'lodash/isEmpty'

lazySizes.cfg.loadHidden = false

// Only init sentry if a release version is present, this indicates we are in stage/prod.
Sentry.init({
    dsn:
        'https://75761b4f5a034e87befcca69ee162529@o367719.ingest.sentry.io/5172011',
    environment: window.React.environment,
    release: process.env.REACT_APP_RELEASE_VERSION,
    ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
    ],
    denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // safari extensions
        /safari-extension\//i,
    ],
})

const cartOpen = () => {
    M.Sidenav.getInstance(document.getElementById('cart')).open()
}

const App = () => {
    useEffect(() => {
        const sidenav = document.querySelectorAll('.sidenav')
        M.Sidenav.init(sidenav, { edge: 'right' })

        // Initialize collapsible (uncomment the line below if you use the dropdown variation)
        const collapsible = document.querySelectorAll('.collapsible')
        M.Collapsible.init(collapsible)

        // Initialize modals
        const modal = document.querySelectorAll('.modal')
        M.Modal.init(modal)

        // initialize materialize select boxes
        const formSelects = document.querySelectorAll('select')
        M.FormSelect.init(formSelects)

        // initialize materialize tooltips
        const tooltips = document.querySelectorAll('.tooltipped')
        M.Tooltip.init(tooltips)

        const dropdowns = document.querySelectorAll('.dropdown-trigger')
        M.Dropdown.init(dropdowns)
    }, [])

    useEffect(() => {
        let cartElem = document.getElementById('cart')
        if (cartElem) {
            M.Sidenav.init(cartElem, {
                edge: 'right',
                onOpenStart: function (el) {
                    cartElem.classList.add('opened')
                }, // A function to be called when sidenav is opened
                onCloseEnd: function (el) {
                    cartElem.classList.remove('opened')
                },
            })

            const navCartBtn = document.querySelector('[data-activates=cart]')
            //cart sidenav button
            navCartBtn.addEventListener('click', function () {
                M.Sidenav.getInstance(cartElem).open()
            })

            const cartCloseElem = document.querySelector('.cart-close')
            cartCloseElem.addEventListener('click', function () {
                M.Sidenav.getInstance(cartElem).close()
            })
        }
    })

    const LazyLoad = ({ component: Component, ...rest }) => (
        <>
            <Suspense fallback={<div>Loading...</div>}>
                <Component {...rest} />
            </Suspense>
        </>
    )

    const BuyGiftCards = lazy(() =>
        import('./BuyGiftCards' /* webpackChunkName: "buy-gift-cards" */)
    )
    const LazyBuyGiftCards = (props) => (
        <LazyLoad component={BuyGiftCards} cartOpen={cartOpen} {...props} />
    )
    const OpenLoopPlastic = lazy(() =>
        import(
            './BuyOpenLoopPlasticCards' /* webpackChunkName: "buy-open-loop-plastic-cards" */
        )
    )
    const LazyOpenLoopPlastic = (props) => (
        <LazyLoad component={OpenLoopPlastic} cartOpen={cartOpen} {...props} />
    )
    const OpenLoopVirtual = lazy(() =>
        import(
            './BuyOpenLoopVirtualCards' /* webpackChunkName: "buy-open-loop-virtual-cards" */
        )
    )
    const LazyOpenLoopVirtual = (props) => (
        <LazyLoad component={OpenLoopVirtual} cartOpen={cartOpen} {...props} />
    )

    const OrderStatus = lazy(() =>
        import('./OrderStatus' /* webpackChunkName: "order-status" */)
    )
    const LazyOrderStatus = (props) => (
        <LazyLoad component={OrderStatus} {...props} />
    )

    const SmallBusinessApply = lazy(() =>
        import('./SmallBusinessApply' /* webpackChunkName: "smb-apply" */)
    )
    const LazySmallBusinessApply = (props) => (
        <LazyLoad component={SmallBusinessApply} {...props} />
    )

    const AlgoliaSearch = lazy(() =>
        import(
            './components/AlgoliaSearch' /* webpackChunkName: "algolia-search" */
        )
    )
    const LazyAlgoliaSearch = (props) => (
        <LazyLoad component={AlgoliaSearch} {...props} />
    )
    const AlgoliaSearchLocal = lazy(() =>
        import(
            './components/AlgoliaSearchLocal' /* webpackChunkName: "algolia-search-local" */
        )
    )
    const LazyAlgoliaSearchLocal = (props) => (
        <LazyLoad component={AlgoliaSearchLocal} {...props} />
    )

    const SMBOrderFlow = lazy(() =>
        import(
            './containers/SMBOrderFlow' /* webpackChunkName: "smb-order-flow" */
        )
    )
    const LazySMBOrderFlow = (props) => (
        <LazyLoad component={SMBOrderFlow} {...props} />
    )
    const SMBOrderFlowLanding = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowLanding' /* webpackChunkName: "smb-order-flow-landing" */
        )
    )
    const LazySMBOrderFlowLanding = (props) => (
        <LazyLoad component={SMBOrderFlowLanding} {...props} />
    )
    const SMBOrderFlowProductDetails = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowProductDetails' /* webpackChunkName: "smb-order-flow-product-details" */
        )
    )
    const LazySMBOrderFlowProductDetails = (props) => (
        <LazyLoad component={SMBOrderFlowProductDetails} {...props} />
    )
    const SMBOrderFlowCustomizeProduct = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowCustomizeProduct' /* webpackChunkName: "smb-order-flow-customize-product" */
        )
    )
    const LazySMBOrderFlowCustomizeProduct = (props) => (
        <LazyLoad component={SMBOrderFlowCustomizeProduct} {...props} />
    )
    const SMBOrderFlowPlaceOrder = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/Components/Checkout/StripeCheckoutWrapper' /* webpackChunkName: "smb-order-flow-place-order" */
        )
    )
    const LazySMBOrderFlowPlaceOrder = (props) => (
        <LazyLoad component={SMBOrderFlowPlaceOrder} {...props} />
    )
    const SMBOrderFlowConfirmation = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowConfirmation' /* webpackChunkName: "smb-order-flow-confirmation" */
        )
    )
    const LazySMBOrderFlowConfirmation = (props) => (
        <LazyLoad component={SMBOrderFlowConfirmation} {...props} />
    )
    const SMBOrderFlowCancelled = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowOrderCancelled' /* webpackChunkName: "smb-order-flow-cancelled" */
        )
    )
    const LazySMBOrderFlowCancelled = (props) => (
        <LazyLoad component={SMBOrderFlowCancelled} {...props} />
    )

    const SMBOrderDetails = lazy(() =>
        import(
            './components/SmallBusiness/SMBOrderDetails' /* webpackChunkName: "smb-order-details" */
        )
    )
    const LazySMBOrderDetails = (props) => (
        <LazyLoad component={SMBOrderDetails} {...props} />
    )

    const LazySMBOrderFlowCobrandCards = (props) => (
        <LazyLoad component={SMBOrderFlowCobrandCards} {...props} />
    )
    const SMBOrderFlowCobrandCards = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowCobrandCards' /* webpackChunkName: "smb-order-flow-cobrand-cards" */
        )
    )

    const PLALandingPage = lazy(() =>
        import(
            './containers/PLALandingPage' /* webpackChunkName: "pla-landing-page-container" */
        )
    )
    const LazyPLALandingPage = (props) => (
        <LazyLoad component={PLALandingPage} {...props} />
    )
    const VisaPLAPage = lazy(() =>
        import(
            './components/PLALandingPages/VisaPLAPage' /* webpackChunkName: "visa-pla-landing-page" */
        )
    )
    const LazyVisaPLAPage = (props) => (
        <LazyLoad component={VisaPLAPage} {...props} />
    )
    const MastercardPLAPage = lazy(() =>
        import(
            './components/PLALandingPages/MastercardPLAPage' /* webpackChunkName: "mastercard-pla-landing-page" */
        )
    )
    const LazyMastercardPLAPage = (props) => (
        <LazyLoad component={MastercardPLAPage} {...props} />
    )

    const createACardProps = {
        siteName: window.React.siteName || '',
        buildACardConfig: window.React.buildACardConfig
            ? JSON.parse(window.React.buildACardConfig)
            : {},
        baseUrl: window.React.base_url || '',
        brandLogoUrl: window.React.brandLogoUrl || '',
        carrierImageUrl: window.React.carrierImageUrl || '',
        carrierImageWithCardUrl: window.React.carrierImageWithCardUrl || '',
        imageGuidelinesUrl: window.React.imageGuidelinesUrl || '',
        faqUrl: window.React.faqUrl || '',
        customQuantity: window.React.customQuantity || false,
        maxQuantity: window.React.maxQuantity || 20,
        availableShippingMethods: window.React.availableShippingMethods
            ? JSON.parse(window.React.availableShippingMethods)
            : [],
        availableCardNetworks: window.React.availableCardNetworks
            ? JSON.parse(window.React.availableCardNetworks)
            : [],
        addToCartUrl: window.React.addToCartUrl || '',
        checkoutUrl: window.React.checkoutUrl || '',
        addToCart: createACardAddToCart,
        addToCartCallback: (response) => {
            window.location = window.React.checkoutUrl || '/'
        },
        addToCartErrorHandler: addToCartErrorHandler,
        imageUploadUrl: window.React.imageUploadUrl || '',
        includeShipping: window.React.includeShipping || true,
        footerContent: (
            <CreateACardFooterContent baseUrl={window.React.base_url || ''} />
        ),
    }

    const CreateACard = lazy(() =>
        import(
            './containers/CreateACard' /* webpackChunkName: "create-a-card-container" */
        )
    )
    const LazyCreateACard = (props) => (
        <LazyLoad component={CreateACard} {...props} />
    )
    const CreateACardLanding = lazy(() =>
        import(
            './components/CreateACardLanding' /* webpackChunkName: "create-a-card-landing" */
        )
    )
    const LazyCreateACardLanding = (props) => (
        <LazyLoad component={CreateACardLanding} {...props} />
    )
    const CreateACardCarrier = lazy(() =>
        import(
            './components/CreateACardCarrier' /* webpackChunkName: "create-a-card-carrier" */
        )
    )
    const LazyCreateACardCarrier = (props) => (
        <LazyLoad component={CreateACardCarrier} {...props} />
    )
    const CreateACardReview = lazy(() =>
        import(
            './components/CreateACardReview' /* webpackChunkName: "create-a-card-review" */
        )
    )
    const LazyCreateACardReview = (props) => (
        <LazyLoad component={CreateACardReview} {...props} />
    )

    const smbOrderFlowProps = {
        siteName: window.React.siteName || '',
        baseUrl: window.React.base_url || '',
        brandLogoUrl: window.React.brandLogoUrl || '',
        cobrandProductId: window.React.cobrandProductId || '',
        footerContent: (
            <SMBOrderFlowFooterContent baseUrl={window.React.base_url || ''} />
        ),
    }

    const SMBOrderFlowLogin = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowLogin' /* webpackChunkName: "smb-order-flow-login" */
        )
    )
    const LazySMBOrderFlowLogin = () => (
        <LazyLoad component={SMBOrderFlowLogin} {...smbOrderFlowProps} />
    )
    const SMBOrderFlowRegistration = lazy(() =>
        import(
            './components/SmallBusiness/OrderFlow/SMBOrderFlowRegistration' /* webpackChunkName: "smb-order-flow-registration" */
        )
    )
    const LazySMBOrderFlowRegistration = () => (
        <LazyLoad component={SMBOrderFlowRegistration} {...smbOrderFlowProps} />
    )

    const SMBCobrandCards = lazy(() =>
        import(
            './components/SmallBusiness/SMBCobrandCards' /* webpackChunkName: "smb-cobrand-cards" */
        )
    )
    const LazySMBCobrandCards = (props) => (
        <LazyLoad component={SMBCobrandCards} {...props} />
    )

    return (
        <React.Fragment>
            <Router>
                <Switch>
                    <Route
                        exact
                        path={'/buy-gift-cards/visa/'}
                        component={LazyOpenLoopPlastic}
                    />
                    <Route
                        exact
                        path={'/buy-gift-cards/mastercard/'}
                        component={LazyOpenLoopPlastic}
                        cartOpen={cartOpen}
                    />
                    <Route
                        path={'/buy-gift-cards/:slug/'}
                        component={LazyBuyGiftCards}
                    />
                    <Route
                        exact
                        path={'/buy-gift-cards/'}
                        component={LazyAlgoliaSearch}
                    />
                    <Route
                        exact
                        path={'/e-gift-cards/'}
                        component={LazyAlgoliaSearch}
                    />
                    <Route
                        path={'/category/:slug'}
                        component={LazyAlgoliaSearch}
                    />
                    <Route
                        path={'/buy-local-gift-cards/'}
                        component={LazyAlgoliaSearchLocal}
                    />
                    <Route
                        exact
                        path={'/visa-virtual-gift-card/'}
                        component={LazyOpenLoopVirtual}
                        cartOpen={cartOpen}
                    />
                    <Route
                        exact
                        path={'/mastercard-virtual-gift-card/'}
                        component={LazyOpenLoopVirtual}
                        cartOpen={cartOpen}
                    />
                    <Route
                        exact
                        path={'/orderstatus/'}
                        component={LazyOrderStatus}
                    />
                    <Route
                        exact
                        path={'/additional-verification-request/'}
                        component={LazySmallBusinessApply}
                    />
                    {!isEmpty(createACardProps.buildACardConfig) ? (
                        <Route path={'/build-a-card/'}>
                            <LazyCreateACard {...createACardProps}>
                                <Switch>
                                    <Route
                                        exact
                                        path={'/build-a-card/'}
                                        component={LazyCreateACardLanding}
                                    />
                                    <Route
                                        exact
                                        path={'/build-a-card/carrier/'}
                                        component={LazyCreateACardCarrier}
                                    />
                                    <Route
                                        exact
                                        path={'/build-a-card/review/'}
                                        component={LazyCreateACardReview}
                                    />
                                    <Route
                                        exact
                                        path={'/build-a-card/:slug/'}
                                        component={LazyCreateACardLanding}
                                    />
                                </Switch>
                            </LazyCreateACard>
                        </Route>
                    ) : null}
                    <Route
                        exact
                        path={'/gift-cards-for-business/login'}
                        component={LazySMBOrderFlowLogin}
                    />
                    <Route
                        exact
                        path={'/gift-cards-for-business/registration'}
                        component={LazySMBOrderFlowRegistration}
                    />
                    <Route path={'/gift-cards-for-business/'}>
                        <LazySMBOrderFlow {...smbOrderFlowProps}>
                            <Route
                                exact
                                path={'/gift-cards-for-business/'}
                                component={LazySMBOrderFlowLanding}
                            />
                            <Route
                                exact
                                path={'/gift-cards-for-business/cobrand-cards/'}
                                component={LazySMBOrderFlowCobrandCards}
                            />
                            <Route
                                exact
                                path={'/gift-cards-for-business/details/'}
                                component={LazySMBOrderFlowProductDetails}
                            />
                            <Route
                                exact
                                path={'/gift-cards-for-business/customize/'}
                                component={LazySMBOrderFlowCustomizeProduct}
                            />
                            <Route
                                exact
                                path={'/gift-cards-for-business/place-order/'}
                                component={LazySMBOrderFlowPlaceOrder}
                            />
                            <Route
                                exact
                                path={'/gift-cards-for-business/confirmation/'}
                                component={LazySMBOrderFlowConfirmation}
                            />
                            <Route
                                exact
                                path={
                                    '/gift-cards-for-business/order-cancelled/'
                                }
                                component={LazySMBOrderFlowCancelled}
                            />
                        </LazySMBOrderFlow>
                    </Route>
                    <Route
                        exact
                        path={'/account/orders/:orderNumber/'}
                        component={LazySMBOrderDetails}
                    />
                    <Route
                        path={[
                            '/buy-visa-gift-card/',
                            '/buy-mastercard-gift-card/',
                        ]}
                    >
                        <LazyPLALandingPage>
                            <Route
                                exact
                                path={'/buy-visa-gift-card/'}
                                component={LazyVisaPLAPage}
                            />
                            <Route
                                exact
                                path={'/buy-mastercard-gift-card/'}
                                component={LazyMastercardPLAPage}
                            />
                        </LazyPLALandingPage>
                    </Route>
                    <Route
                        exact
                        path={'/account/cobrand-cards/'}
                        component={LazySMBCobrandCards}
                    />
                </Switch>
            </Router>
            <Suspense fallback={<div>Loading...</div>}>
                <SiteSearch />
            </Suspense>
        </React.Fragment>
    )
}

export default compose(WithErrors, withRouter)(App)
